import { fork, takeLatest, call, put, all, select } from "redux-saga/effects";
import analyticsApi from "./analyticsApi";

import {
  fetchBasicAnalyticsRequest,
  fetchBasicAnalyticsSuccess,
  fetchBasicAnalyticsFailure,
  fetchPracticeSolutionRequest,
  fetchPracticeSolutionSuccess,
  fetchPracticeSolutionFailure,
  fetchTopicWiseComparisonRequest,
  fetchTopicWiseComparisonSuccess,
  fetchTopicWiseComparisonFailure,
  fetchSubjectWiseAnalysisRequest,
  fetchSubjectWiseAnalysisSuccess,
  fetchSubjectWiseAnalysisFailure,
  fetchOverallAnalyticsRequest,
  fetchOverallAnalyticsSuccess,
  fetchOverallAnalyticsFailure,
  fetchQuestionAnalyticsRequest,
  fetchQuestionAnalyticsSuccess,
  fetchQuestionAnalyticsFailure,
} from "./analyticsSlice";

/* ================= PAYLOAD BUILDER ================= */
const buildPyqPayload = (state) => {
  const paperType = state.testType?.id;
  const isAITS = paperType === 3;
  const isTopicWisePyq = Boolean(state.testDetails?.setId);

  /* ---------- AITS ---------- */
  if (isAITS) {
    return {
      paperType,
      paperId: state.testDetails?.paperId || state.paperId,
      plannerTestId: state.testDetails?.planner_test_id,
      isOffline: 0,
      // attemptCount: state.analytics?.selectedAttemptId ?? 0,
      attemptCount: 1,

    };
  }

  /* ---------- PYQ (UNCHANGED) ---------- */
  return {
    paperType,
    paperId: isTopicWisePyq ? undefined : state.paperId,
    setId: isTopicWisePyq ? state.testDetails.setId : undefined,
   
    //  selectedAttemptId:
    // state.analyticsSlice?.basicAnalytics?.data?.attempts?.[0]?.id,
  };
};

/* ---------------- BASIC ANALYTICS ---------------- */
// function* fetchBasicAnalyticsWorker() {
//   try {
//     const state = yield select((s) => s.examSlice);
//     const response = yield call(
//       analyticsApi.getBasicAnalytics,
//       buildPyqPayload(state)
//     );
//     yield put(fetchBasicAnalyticsSuccess(response));
//   } catch (e) {
//     yield put(fetchBasicAnalyticsFailure(e.message));
//   }
// }
function* fetchBasicAnalyticsWorker(action) {
  try {
    const state = yield select((s) => s.examSlice);
    let payload = buildPyqPayload(state);

    if (action.payload?.paper_type) {
      payload.paperType = action.payload.paper_type;
    }
    if (action.payload?.paper_id) {
      payload.paperId = action.payload.paper_id;
      payload.setId = undefined;
    }
    if (action.payload?.planner_test_id) {
      payload.plannerTestId = action.payload.planner_test_id;
    }
    if (action.payload?.set_id) {
      payload = {
        ...payload,
        paperType: action.payload.paper_type,
        setId: action.payload.set_id,
        paperId: undefined, 
      };
    }
    if (action.payload?.attempt_count !== undefined) {
      payload.attemptCount = action.payload.attempt_count;
    } else if (action.payload?.selected_attempt !== undefined) {
      // Backward compatibility for old callers.
      payload.attemptCount = Number(action.payload.selected_attempt) + 1;
    }

    const response = yield call(
      analyticsApi.getBasicAnalytics,
      payload
    );

    yield put(fetchBasicAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchBasicAnalyticsFailure(e.message));
  }
}

/* ---------------- PRACTICE SOLUTION ---------------- */
function* fetchPracticeSolutionWorker() {
  try {
    const state = yield select((s) => s.examSlice);
    const response = yield call(
      analyticsApi.getPracticeSolution,
      buildPyqPayload(state)
    );
    yield put(fetchPracticeSolutionSuccess(response));
  } catch (e) {
    yield put(fetchPracticeSolutionFailure(e.message));
  }
}

/* ---------------- TOPIC WISE ---------------- */
function* fetchTopicWiseComparisonWorker() {
  try {
    const state = yield select((s) => s.examSlice);
    const response = yield call(
      analyticsApi.getTopicWiseComparison,
      buildPyqPayload(state)
    );
    yield put(fetchTopicWiseComparisonSuccess(response));
  } catch (e) {
    yield put(fetchTopicWiseComparisonFailure(e.message));
  }
}

/* ---------------- SUBJECT WISE ---------------- */
function* fetchSubjectWiseAnalysisWorker() {
  try {
    const state = yield select((s) => s.examSlice);
    const response = yield call(
      analyticsApi.getSubjectWiseComparison,
      buildPyqPayload(state)
    );
    yield put(fetchSubjectWiseAnalysisSuccess(response));
  } catch (e) {
    yield put(fetchSubjectWiseAnalysisFailure(e.message));
  }
}

/* ---------------- OVERALL ---------------- */
function* fetchOverallAnalyticsWorker(action) {
  try {
    const state = yield select((s) => s.examSlice);
    const response = yield call(
      analyticsApi.getOverallAnalytics,
      {
        ...buildPyqPayload(state),
        filter_by: action.payload?.filter_by || "tpq",
      }
    );
    yield put(fetchOverallAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchOverallAnalyticsFailure(e.message));
  }
}

/* ---------------- QUESTION ANALYTICS ---------------- */
function* fetchQuestionAnalyticsWorker(action) {
  try {
    const state = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const resolvedSetId =
      action.payload?.setId ||
      state.testDetails?.setId ||
      state.testDetails?.set_id;
    const resolvedPaperId =
      action.payload?.paperId ||
      state.paperId ||
      state.testDetails?.paper_id ||
      state.testDetails?.paperId;
    const selectedFromBasic =
      analyticsState.basicAnalytics?.data?.selected_attempt;
    const attemptCount =
      action.payload?.attempt_count !== undefined
        ? action.payload.attempt_count
        : selectedFromBasic !== undefined && selectedFromBasic !== null
        ? Number(selectedFromBasic) + 1
        : 1;
    const response = yield call(analyticsApi.getQuestionAnalytics, {
      paperType: action.payload?.paperType || state.testType?.id,
      paperId: resolvedPaperId,
      setId: resolvedSetId,
      qid: action.payload.qid,
      isOffline: action.payload?.is_offline ?? 0,
      attemptCount,
    });
    yield put(fetchQuestionAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchQuestionAnalyticsFailure(e.message));
  }
}

/* ---------------- WATCHER ---------------- */
function* watchAnalyticsSaga() {
  yield takeLatest(fetchBasicAnalyticsRequest.type, fetchBasicAnalyticsWorker);
  yield takeLatest(fetchPracticeSolutionRequest.type, fetchPracticeSolutionWorker);
  yield takeLatest(fetchTopicWiseComparisonRequest.type, fetchTopicWiseComparisonWorker);
  yield takeLatest(fetchSubjectWiseAnalysisRequest.type, fetchSubjectWiseAnalysisWorker);
  yield takeLatest(fetchOverallAnalyticsRequest.type, fetchOverallAnalyticsWorker);
  yield takeLatest(fetchQuestionAnalyticsRequest.type, fetchQuestionAnalyticsWorker);
}

export default function* analyticsSaga() {
  yield all([fork(watchAnalyticsSaga)]);
}
